import { observer } from "owa-mobx-react";
import folderStore, { getEffectiveFolderDisplayName } from "owa-folders";
import { getCustomBundledIcon } from "owa-folders-common/lib/util/getCustomIconComponent";
import { FolderNode } from "owa-mail-folder-view";
import { getFolderViewStateFromIdNoFallback } from "owa-mail-folder-store/lib/selectors/getFolderViewStateFromId";
import { getCustomFolderColorHexValue } from "owa-mail-folder-store/lib/selectors/getCustomFolderColor";
import React from "react";
import { useResizeObserver } from "owa-react-hooks/lib/useResizeObserver";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import type { FavoritesSideRailItem } from "../types/FavoritesSideRailItem";
import getSiderailFavoriteTooltipText, { getSiderailFolderTooltipText, } from "../util/getSiderailFavoriteTooltipText";
import FavoriteNode from "./FavoriteNode";
import { sideRailNodeWrapper } from "./FavoritesSideRail.scss";

interface SharedFavoritesSideRailNodeProps {
    itemKey: string;
    hideIcon: boolean;
    onHeightChange: (itemKey: string, height: number) => void;
}

type FavoritesSideRailNodeProps = SharedFavoritesSideRailNodeProps & ({
    kind: "favorite";
    favoriteId: string;
} | {
    kind: "folder";
    folderId: string;
    treeType: Extract<FavoritesSideRailItem, {
        kind: "folder";
    }>["treeType"];
});

const FavoritesSideRailNode: React.FunctionComponent<FavoritesSideRailNodeProps> = observer(function FavoritesSideRailNode(props) {
    const { itemKey, hideIcon, onHeightChange } = props;
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    
    const onResize = React.useCallback((bounds: DOMRectReadOnly) => {
        onHeightChange(itemKey, bounds.height);
    }, [itemKey, onHeightChange]);
    useResizeObserver("FavoritesSideRailNode", wrapperRef, onResize);
    
    if (props.kind === "favorite") {
        return (<div ref={wrapperRef} className={sideRailNodeWrapper}>
                    <FavoriteNode favoriteId={props.favoriteId} isVerticalNode={true} hideIcon={hideIcon} actionSource="FavoritesSiderail" customTreeNodeTooltip={getSiderailFavoriteTooltipText(props.favoriteId)}/>
                </div>);
    }
    
    const folder = folderStore().folderTable.get(props.folderId);
    if (!folder) {
        return null;
    }
    
    const shouldHideIcon = hideIcon || isWin32StylingEnabled();
    const customIconComponent = shouldHideIcon
        ? undefined
        : getCustomBundledIcon(props.folderId, true);
    const customIconColor = getCustomFolderColorHexValue(getFolderViewStateFromIdNoFallback(props.folderId)?.color);
    
    return (<div ref={wrapperRef} className={sideRailNodeWrapper}>
                <FolderNode customIconComponent={customIconComponent} customIconColor={customIconColor} displayName={getEffectiveFolderDisplayName(folder)} folderId={props.folderId} treeType={props.treeType} distinguishedFolderId={folder.distinguishedFolderType ?? ""} totalCount={folder.totalMessageCount} unreadCount={folder.UnreadCount} isVerticalNode={true} hideIcon={shouldHideIcon} customTreeNodeTooltip={getSiderailFolderTooltipText(props.folderId)}/>
            </div>);
}, "FavoritesSideRailNode");

export default FavoritesSideRailNode;
